import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, canWrite } from "@/lib/auth";
import { DeleteRecordButton } from "@/components/DeleteRecordButton";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Save, ShieldAlert, Search, ListChecks,
  Send, Plus, Trash2, ArrowUp, ArrowDown, RefreshCw, History, FileCheck2, AlertTriangle,
} from "lucide-react";
import { SyncStatusBadge } from "@/pages/SyncCommandDesk";
import { AssetPathwayBadge } from "@/components/AssetPathwayBadge";
import { ApproveSendDialog } from "@/components/ApproveSendDialog";

// ---------------------------------------------------------------------------
// MP12 Module 4 — Sync Brief editor (multi-tab).
// Tabs: Brief · Matching · Review File · Approval · History.
// Internal-only. Sensitive fields are redacted server-side for non-authorised
// viewers, so we can render whatever comes back.
// ---------------------------------------------------------------------------

const BRIEF_STATUS_LABELS = {
  DRAFT:                      "DRAFT",
  IN_REVIEW:                  "IN REVIEW",
  MATCHING:                   "MATCHING",
  REVIEW_FILE_IN_PREPARATION: "REVIEW FILE IN PREPARATION",
  READY_FOR_APPROVAL:         "READY FOR APPROVAL",
  APPROVED_SENT:              "APPROVED / SENT",
  CLOSED_EXPIRED:             "CLOSED / EXPIRED",
  DECLINED:                   "DECLINED",
};

export default function SyncBriefEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const writer = canWrite(user);

  const [brief, setBrief] = useState(null);
  const [vocab, setVocab] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [approveOpen, setApproveOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, v] = await Promise.all([
        api.get(`/sync-briefs/${id}`),
        api.get(`/sync-briefs/meta/vocab`),
      ]);
      setBrief(b.data);
      setVocab(v.data);
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load sync brief.");
      navigate("/advanced/sync-command-desk");
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  function patch(fields) {
    setBrief((b) => ({ ...(b || {}), ...fields }));
    setDirty(true);
  }

  async function save() {
    if (!brief) return;
    setSaving(true);
    try {
      const payload = { ...brief };
      // Never PATCH bookkeeping/read-only fields
      ["id","internal_brief_id","status","created_at","updated_at","created_by","last_updated_by",
       "last_snapshot_id","last_snapshot_version","last_snapshot_at","last_snapshot_by"]
        .forEach((k) => delete payload[k]);
      const r = await api.patch(`/sync-briefs/${id}`, payload);
      setBrief(r.data);
      setDirty(false);
      toast.success("Sync brief saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally { setSaving(false); }
  }

  async function changeStatus(newStatus) {
    if (dirty) { toast.error("Save changes before changing status."); return; }
    const reason = window.prompt(`Reason (optional) for status → ${BRIEF_STATUS_LABELS[newStatus] || newStatus}:`, "") || "";
    setBusy(true);
    try {
      const r = await api.post(`/sync-briefs/${id}/status`, { status: newStatus, reason });
      setBrief(r.data);
      toast.success(`Status → ${BRIEF_STATUS_LABELS[newStatus] || newStatus}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status change refused.");
    } finally { setBusy(false); }
  }

  const allowedNext = useMemo(() => {
    if (!vocab || !brief) return [];
    const map = vocab.allowed_transitions || {};
    return map[brief.status] || [];
  }, [vocab, brief]);

  if (!writer) return <div className="p-6 text-[13px] text-muted-foreground">Sync Command Desk is limited to authorised internal users.</div>;
  if (loading || !brief) return <div className="p-6 text-[13px] text-muted-foreground">Loading…</div>;

  const editable = !["APPROVED_SENT","CLOSED_EXPIRED","DECLINED"].includes(brief.status);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="sync-brief-editor-page">
      <div className="flex items-center gap-2 flex-wrap">
        <Button asChild variant="ghost" size="sm" data-testid="sbe-back">
          <Link to="/advanced/sync-command-desk"><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back</Link>
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[11px] text-muted-foreground">{brief.internal_brief_id}</span>
            <h1 className="font-display text-[20px] truncate">{brief.media_project_context || "(no context)"}</h1>
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-3">
            {brief.opportunity_source ? <span>Source: {brief.opportunity_source}</span> : null}
            {brief.media_type          ? <span>Media: {brief.media_type}</span> : null}
            {brief.deadline            ? <span>Deadline: {brief.deadline}</span> : null}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SyncStatusBadge value={brief.status} />
          {dirty && <span className="text-[11px] text-amber-800/80">Unsaved changes</span>}
          {editable && (
            <Button size="sm" onClick={save} disabled={!dirty || saving} style={{ background: "hsl(347 72% 22%)" }} data-testid="sbe-save">
              <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
            </Button>
          )}
          {editable && allowedNext.length > 0 && (
            <Select onValueChange={changeStatus} disabled={busy || dirty}>
              <SelectTrigger className="h-8 w-[220px] text-[12px]" data-testid="sbe-status-select"><SelectValue placeholder="Change status…" /></SelectTrigger>
              <SelectContent>
                {allowedNext.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`sbe-status-next-${s}`}>{BRIEF_STATUS_LABELS[s] || s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {brief.status === "READY_FOR_APPROVAL" && (user?.role === "super_admin" || user?.role === "catalog_manager") && (
            <Button size="sm" onClick={() => setApproveOpen(true)} disabled={dirty} style={{ background: "hsl(347 72% 22%)" }} data-testid="sbe-approve-send">
              <Send className="w-3.5 h-3.5 mr-1.5" /> APPROVED / SEND
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="brief" className="w-full">
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="brief"       data-testid="sbe-tab-brief">Brief</TabsTrigger>
          <TabsTrigger value="matching"    data-testid="sbe-tab-matching"><Search className="w-3.5 h-3.5 mr-1" />Matching</TabsTrigger>
          <TabsTrigger value="review-file" data-testid="sbe-tab-review-file"><ListChecks className="w-3.5 h-3.5 mr-1" />Review File</TabsTrigger>
          <TabsTrigger value="history"     data-testid="sbe-tab-history"><History className="w-3.5 h-3.5 mr-1" />History</TabsTrigger>
        </TabsList>

        <TabsContent value="brief" className="mt-3">
          <BriefTab brief={brief} patch={patch} editable={editable} isSensitiveViewer={user?.role === "super_admin" || user?.role === "catalog_manager"} />
        </TabsContent>
        <TabsContent value="matching" className="mt-3">
          <MatchingTab briefId={id} brief={brief} editable={editable} onAssetAdded={load} />
        </TabsContent>
        <TabsContent value="review-file" className="mt-3">
          <ReviewFileTab briefId={id} brief={brief} editable={editable} />
        </TabsContent>
        <TabsContent value="history" className="mt-3">
          <HistoryTab briefId={id} brief={brief} />
        </TabsContent>
      </Tabs>

      {user?.role === "super_admin" && brief?.id && (
        <DeleteRecordButton
          recordType="sync_brief"
          recordLabel="Sync Brief / Opportunity"
          endpoint={`/sync-briefs/${id}`}
          confirmToken={brief?.reference || (brief?.id || "").slice(0, 8).toUpperCase()}
          confirmTokenLabel="brief reference"
          isSuperAdmin={true}
          requireTypedConfirmation={true}
          cascadePreview={[
            { label: "Review-file assets", count: (brief?.review_file?.assets || []).length },
            { label: "History / audit entries (preserved as tombstone)", count: undefined },
          ].filter((r) => r.count === undefined ? true : r.count > 0)}
          onDeleted={() => navigate("/advanced/sync-command-desk")}
          testidPrefix="sync-brief-delete"
        />
      )}

      <ApproveSendDialog
        open={approveOpen}
        onOpenChange={setApproveOpen}
        brief={brief}
        onApproved={() => { load(); }}
      />
    </div>
  );
}

// ------------------- Brief tab -------------------

function Field({ label, value, onChange, multiline, rows = 2, testid, disabled }) {
  return (
    <div>
      <Label className="text-[10.5px] uppercase tracking-wide">{label}</Label>
      {multiline ? (
        <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} className="mt-1 text-[12px]" data-testid={testid} readOnly={disabled} />
      ) : (
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 h-8 text-[12px]" data-testid={testid} readOnly={disabled} />
      )}
    </div>
  );
}

function BriefTab({ brief, patch, editable, isSensitiveViewer }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-[15px]">Sync Brief</CardTitle>
        <p className="text-[11px] italic text-muted-foreground">Sensitive identity fields (CISN / CIS-IDN, client name) are visible to Super Admin and Catalog Manager only. Other viewers see a redacted placeholder.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Media / project context" value={brief.media_project_context} onChange={(v) => patch({ media_project_context: v })} multiline testid="sbe-context" disabled={!editable} />
          <Field label="Opportunity source"      value={brief.opportunity_source}    onChange={(v) => patch({ opportunity_source: v })}    testid="sbe-source" disabled={!editable} />
          <Field label="Media type"              value={brief.media_type}            onChange={(v) => patch({ media_type: v })}            testid="sbe-media" disabled={!editable} />
          <Field label="Deadline"                value={brief.deadline}              onChange={(v) => patch({ deadline: v })}              testid="sbe-deadline" disabled={!editable} />
          <Field label="Assigned internal reviewer (email)" value={brief.assigned_internal_reviewer} onChange={(v) => patch({ assigned_internal_reviewer: v })} testid="sbe-reviewer" disabled={!editable} />
        </div>

        <div className="border rounded p-3" style={{ background: "hsl(0 40% 97%)", borderColor: "hsl(0 40% 84%)" }}>
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="w-4 h-4" style={{ color: "hsl(0 60% 32%)" }} />
            <div className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: "hsl(0 60% 32%)" }}>Restricted identity</div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="CISN / CIS-IDN (restricted)" value={brief.cisn_reference} onChange={(v) => patch({ cisn_reference: v })} testid="sbe-cisn" disabled={!editable || !isSensitiveViewer} />
            <Field label="Client name (only when authorised)" value={brief.client_name} onChange={(v) => patch({ client_name: v })} testid="sbe-client-name" disabled={!editable || !isSensitiveViewer} />
          </div>
          <div className="flex items-center gap-2 mt-3 border rounded p-2 bg-white">
            <Switch checked={!!brief.project_name_authorised} onCheckedChange={(v) => patch({ project_name_authorised: !!v })} disabled={!editable} data-testid="sbe-project-name-auth" />
            <Label className="text-[12px]">Storing project name is authorised</Label>
          </div>
          {brief.project_name_authorised && (
            <div className="mt-2">
              <Field label="Project name" value={brief.project_name} onChange={(v) => patch({ project_name: v })} testid="sbe-project-name" disabled={!editable} />
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Genre"           value={brief.genre}                onChange={(v) => patch({ genre: v })}           testid="sbe-genre" disabled={!editable} />
          <Field label="Mood"            value={brief.mood}                 onChange={(v) => patch({ mood: v })}            testid="sbe-mood" disabled={!editable} />
          <Field label="Instrumentation" value={brief.instrumentation}      onChange={(v) => patch({ instrumentation: v })} testid="sbe-instrument" disabled={!editable} />
          <Field label="Vocal requirement"     value={brief.vocal_requirement}     onChange={(v) => patch({ vocal_requirement: v })}     testid="sbe-vocal" disabled={!editable} />
          <Field label="Lyrical requirement"   value={brief.lyrical_requirement}   onChange={(v) => patch({ lyrical_requirement: v })}   testid="sbe-lyrical" disabled={!editable} />
          <Field label="Lyrics restrictions"   value={brief.lyrics_restrictions}   onChange={(v) => patch({ lyrics_restrictions: v })}   multiline testid="sbe-lyrics-restrictions" disabled={!editable} />
          <Field label="Territory"             value={brief.territory}             onChange={(v) => patch({ territory: v })}             testid="sbe-territory" disabled={!editable} />
          <Field label="Term"                  value={brief.term}                  onChange={(v) => patch({ term: v })}                  testid="sbe-term" disabled={!editable} />
          <Field label="Budget / rate range"   value={brief.budget_or_rate_range}  onChange={(v) => patch({ budget_or_rate_range: v })}  testid="sbe-budget" disabled={!editable} />
          <Field label="Exclusivity requirements" value={brief.exclusivity_requirements} onChange={(v) => patch({ exclusivity_requirements: v })} testid="sbe-exclusivity" disabled={!editable} />
          <Field label="Delivery requirements" value={brief.delivery_requirements} onChange={(v) => patch({ delivery_requirements: v })} multiline testid="sbe-delivery-req" disabled={!editable} />
          <Field label="Restrictions"          value={brief.restrictions}          onChange={(v) => patch({ restrictions: v })}          multiline testid="sbe-restrictions" disabled={!editable} />
          <Field label="Clearance requirements" value={brief.clearance_requirements} onChange={(v) => patch({ clearance_requirements: v })} multiline testid="sbe-clearance-req" disabled={!editable} />
        </div>
        <Field label="Internal notes" value={brief.internal_notes} onChange={(v) => patch({ internal_notes: v })} multiline rows={3} testid="sbe-notes" disabled={!editable} />
      </CardContent>
    </Card>
  );
}

// ------------------- Matching tab -------------------

function MatchingTab({ briefId, brief, editable, onAssetAdded }) {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addingId, setAddingId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sync-briefs/${briefId}/matching`, { params: { q: q.trim() || undefined, limit: 100 } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load matching workspace.");
    } finally { setLoading(false); }
  }, [briefId, q]);

  useEffect(() => { load(); }, [load]);

  async function add(trackId, isAlternate) {
    if (!editable) { toast.error("Brief is locked; cannot add assets."); return; }
    setAddingId(trackId);
    try {
      await api.post(`/sync-briefs/${briefId}/review-file/assets`, {
        track_id: trackId, is_alternate: !!isAlternate,
      });
      toast.success(isAlternate ? "Added as alternate." : "Added to Review File.");
      onAssetAdded && onAssetAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Server refused this asset.");
    } finally { setAddingId(""); }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap gap-2 items-center">
          <CardTitle className="text-[15px]">Catalogue matching</CardTitle>
          <div className="ml-auto flex items-center gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title / artist / catalog code / ISRC…" className="h-8 w-[260px] text-[12px]" data-testid="sbe-match-search" />
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="sbe-match-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh</Button>
          </div>
        </div>
        <p className="text-[11px] italic text-muted-foreground mt-1">
          {data?.disclaimer || "Deterministic rule-based matching only. Metadata suggestions are not clearance or legal confirmation."}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? <div className="text-[12.5px] text-muted-foreground">Loading matches…</div> : null}
        {data && (
          <>
            <div className="flex gap-2 text-[11px]">
              <Badge variant="outline" style={{ background: "hsl(150 45% 92%)", color: "hsl(150 45% 24%)", borderColor: "hsl(150 40% 66%)" }} data-testid="sbe-match-count-include">{data.counts.include} include</Badge>
              <Badge variant="outline" style={{ background: "hsl(40 55% 94%)",  color: "hsl(35 60% 28%)",  borderColor: "hsl(40 55% 76%)" }}  data-testid="sbe-match-count-review">{data.counts.needs_review} needs review</Badge>
              <Badge variant="outline" style={{ background: "hsl(0 55% 94%)",   color: "hsl(0 65% 30%)",   borderColor: "hsl(0 55% 72%)" }}   data-testid="sbe-match-count-exclude">{data.counts.exclude} exclude</Badge>
            </div>
            <MatchingSection title="Include" bucket={data.include} intent="include" adding={addingId} onAdd={add} editable={editable} />
            <MatchingSection title="Needs review" bucket={data.needs_review} intent="needs_review" adding={addingId} onAdd={add} editable={editable} />
            <MatchingSection title="Excluded" bucket={data.exclude} intent="exclude" adding={addingId} onAdd={add} editable={false} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function MatchingSection({ title, bucket, intent, onAdd, editable, adding }) {
  if (!bucket || bucket.length === 0) return null;
  const badgeStyle = intent === "include"
    ? { bg: "hsl(150 45% 92%)", fg: "hsl(150 45% 24%)", border: "hsl(150 40% 66%)" }
    : intent === "needs_review"
    ? { bg: "hsl(40 55% 94%)",  fg: "hsl(35 60% 28%)",  border: "hsl(40 55% 76%)" }
    : { bg: "hsl(0 55% 94%)",   fg: "hsl(0 65% 30%)",   border: "hsl(0 55% 72%)" };
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide font-semibold mb-1" style={{ color: badgeStyle.fg }}>{title}</div>
      <ul className="border rounded divide-y">
        {bucket.map((c) => (
          <li key={c.track_id} className="p-2.5 flex items-start gap-2" data-testid={`sbe-match-row-${c.track_id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[13px] font-medium truncate">{c.title || "(untitled)"}</span>
                <span className="text-[11px] text-muted-foreground">{c.artist}</span>
                <Badge variant="outline" className="text-[10px] font-mono">{c.catalog_code || "—"}</Badge>
                <AssetPathwayBadge value={c.asset_pathway} />
                <Badge variant="outline" className="text-[10px]">{c.clearance_status || "Incomplete"}</Badge>
              </div>
              <ul className="text-[11.5px] text-muted-foreground mt-1 list-disc pl-4 space-y-0.5">
                {c.reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
            <div className="flex flex-col gap-1">
              {intent !== "exclude" && editable && (
                <>
                  <Button size="sm" variant="outline" onClick={() => onAdd(c.track_id, false)} disabled={adding === c.track_id} data-testid={`sbe-match-add-${c.track_id}`}>
                    <Plus className="w-3.5 h-3.5 mr-1" />Add
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onAdd(c.track_id, true)} disabled={adding === c.track_id} data-testid={`sbe-match-add-alt-${c.track_id}`}>
                    Add as alternate
                  </Button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ------------------- Review File tab -------------------

function ReviewFileTab({ briefId, brief, editable }) {
  const [rf, setRf] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sync-briefs/${briefId}/review-file`);
      setRf(r.data.review_file);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load review file."); }
    finally { setLoading(false); }
  }, [briefId]);
  useEffect(() => { load(); }, [load]);

  async function patch(fields) {
    try {
      const r = await api.patch(`/sync-briefs/${briefId}/review-file`, fields);
      setRf(r.data.review_file);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save review-file field."); }
  }
  async function patchAsset(aid, fields) {
    try {
      const r = await api.patch(`/sync-briefs/${briefId}/review-file/assets/${aid}`, fields);
      setRf(r.data.review_file);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update asset."); }
  }
  async function remove(aid) {
    const reason = window.prompt(
      "Removal reason (required): Test record | Duplicate | Entered in error | Client request | No longer required | Other: <text>",
      "Entered in error"
    );
    if (!reason) return;
    try {
      const qs = new URLSearchParams();
      qs.set("reason", reason);
      qs.set("force", "true");
      const r = await api.delete(`/sync-briefs/${briefId}/review-file/assets/${aid}?${qs.toString()}`);
      setRf(r.data.review_file);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove asset."); }
  }
  async function move(aid, dir) {
    if (!rf) return;
    const ids = (rf.assets || []).map((a) => a.id);
    const idx = ids.indexOf(aid);
    if (idx < 0) return;
    const target = idx + dir;
    if (target < 0 || target >= ids.length) return;
    const swapped = [...ids];
    [swapped[idx], swapped[target]] = [swapped[target], swapped[idx]];
    try {
      const r = await api.post(`/sync-briefs/${briefId}/review-file/reorder`, { ordered_asset_ids: swapped });
      setRf(r.data.review_file);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reorder."); }
  }

  if (loading || !rf) return <div className="text-[12.5px] text-muted-foreground">Loading review file…</div>;

  const assets = rf.assets || [];
  const primary = assets.filter((a) => !a.is_alternate);
  const alternate = assets.filter((a) => a.is_alternate);

  return (
    <Card data-testid="sbe-review-file">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <CardTitle className="text-[15px]">Submission Review File</CardTitle>
          <Badge variant="outline" className="text-[10px] uppercase tracking-wide">{primary.length} primary · {alternate.length} alternate</Badge>
        </div>
        <p className="text-[11px] italic text-muted-foreground mt-1">
          The server re-validates every asset at APPROVED / SEND. DEMO ONLY and blocked-pathway assets are refused server-side, regardless of UI state.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="File notes" value={rf.file_notes} onChange={(v) => patch({ file_notes: v })} multiline testid="sbe-rf-file-notes" disabled={!editable} />
          <Field label="Delivery readiness notes" value={rf.delivery_readiness_notes} onChange={(v) => patch({ delivery_readiness_notes: v })} multiline testid="sbe-rf-delivery-notes" disabled={!editable} />
          <Field label="Restrictions notes" value={rf.restrictions_notes} onChange={(v) => patch({ restrictions_notes: v })} multiline testid="sbe-rf-restriction-notes" disabled={!editable} />
        </div>

        {assets.length === 0 && (
          <div className="text-[12.5px] text-muted-foreground italic" data-testid="sbe-rf-empty">No assets yet. Add eligible assets from the Matching tab.</div>
        )}

        {[["Primary assets", primary, false], ["Alternates", alternate, true]].map(([title, list, altSection]) => (
          list.length === 0 ? null : (
            <div key={title}>
              <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1">{title}</div>
              <ul className="border rounded divide-y">
                {list.map((a) => (
                  <li key={a.id} className="p-2.5 flex items-start gap-2" data-testid={`sbe-rf-row-${a.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[13px] font-medium truncate">{a.title_at_add || "(untitled)"}</span>
                        <span className="text-[11px] text-muted-foreground">{a.artist_at_add}</span>
                        <AssetPathwayBadge value={a.asset_pathway_at_add} />
                        <Badge variant="outline" className="text-[10px]">{a.clearance_status_at_add || "Incomplete"}</Badge>
                        {altSection && <Badge variant="outline" className="text-[10px] uppercase tracking-wide">Alternate</Badge>}
                      </div>
                      <div className="grid md:grid-cols-2 gap-2 mt-2">
                        <Textarea value={a.pitch_rationale || ""} onChange={(e) => patchAsset(a.id, { pitch_rationale: e.target.value })} rows={2} placeholder="Pitch rationale" className="text-[12px]" readOnly={!editable} data-testid={`sbe-rf-pitch-${a.id}`} />
                        <Textarea value={a.reviewer_notes || ""}  onChange={(e) => patchAsset(a.id, { reviewer_notes:  e.target.value })} rows={2} placeholder="Reviewer notes" className="text-[12px]" readOnly={!editable} data-testid={`sbe-rf-notes-${a.id}`} />
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-[11px] text-muted-foreground">
                        <Switch checked={!!a.is_alternate} onCheckedChange={(v) => patchAsset(a.id, { is_alternate: !!v })} disabled={!editable} data-testid={`sbe-rf-alt-${a.id}`} />
                        <span>Alternate</span>
                        <Button asChild size="sm" variant="ghost" className="ml-auto text-[11px]" data-testid={`sbe-rf-open-track-${a.id}`}>
                          <Link to={`/tracks/${a.track_id}`}>Open track</Link>
                        </Button>
                      </div>
                    </div>
                    {editable && (
                      <div className="flex flex-col gap-1">
                        <button type="button" onClick={() => move(a.id, -1)} className="h-6 w-6 rounded border text-muted-foreground hover:bg-muted/40" aria-label="Move up" data-testid={`sbe-rf-up-${a.id}`}>
                          <ArrowUp className="w-3 h-3 mx-auto" />
                        </button>
                        <button type="button" onClick={() => move(a.id, 1)} className="h-6 w-6 rounded border text-muted-foreground hover:bg-muted/40" aria-label="Move down" data-testid={`sbe-rf-down-${a.id}`}>
                          <ArrowDown className="w-3 h-3 mx-auto" />
                        </button>
                        <button type="button" onClick={() => remove(a.id)} className="h-6 w-6 rounded border text-muted-foreground hover:text-[hsl(0_70%_38%)]" aria-label="Remove" data-testid={`sbe-rf-remove-${a.id}`}>
                          <Trash2 className="w-3 h-3 mx-auto" />
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )
        ))}

        {brief.status === "REVIEW_FILE_IN_PREPARATION" && primary.length > 0 && (
          <div className="rounded border p-2.5 flex items-start gap-2" style={{ background: "hsl(40 55% 96%)", borderColor: "hsl(40 55% 78%)" }}>
            <FileCheck2 className="w-4 h-4 mt-0.5" style={{ color: "hsl(35 60% 30%)" }} />
            <div className="flex-1">
              <div className="text-[12.5px] font-medium">Ready to move brief to <em>READY FOR APPROVAL</em>?</div>
              <p className="text-[11px] text-muted-foreground">The APPROVED / SEND button appears only once the brief is in READY FOR APPROVAL.</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ------------------- History tab -------------------

function HistoryTab({ briefId, brief }) {
  const [snaps, setSnaps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sync-briefs/${briefId}/snapshots`);
      setSnaps(r.data?.rows || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load snapshots."); }
    finally { setLoading(false); }
  }, [briefId]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card data-testid="sbe-history">
      <CardHeader className="pb-2">
        <CardTitle className="text-[15px]">Submission history</CardTitle>
        <p className="text-[11px] italic text-muted-foreground">Frozen, non-editable snapshots created by APPROVED / SEND. Each entry records exactly what was approved and whether transport released the package.</p>
      </CardHeader>
      <CardContent>
        {loading && <div className="text-[12.5px] text-muted-foreground">Loading…</div>}
        {!loading && snaps.length === 0 && (
          <div className="text-[12.5px] text-muted-foreground italic" data-testid="sbe-history-empty">No submission snapshots yet.</div>
        )}
        <ul className="space-y-2" data-testid="sbe-history-list">
          {snaps.map((s) => (
            <li key={s.id} className="border rounded p-3" data-testid={`sbe-snap-${s.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="text-[10px] uppercase tracking-wide">v{s.version}</Badge>
                <Badge variant="outline" className="text-[10px] uppercase tracking-wide"
                  style={
                    s.delivery_state === "SENT"
                      ? { background: "hsl(150 45% 92%)", color: "hsl(150 45% 24%)", borderColor: "hsl(150 40% 66%)" }
                      : { background: "hsl(0 55% 94%)",   color: "hsl(0 65% 30%)",   borderColor: "hsl(0 55% 72%)" }
                  }
                  data-testid={`sbe-snap-state-${s.id}`}
                >
                  {s.delivery_state}
                </Badge>
                <span className="text-[11px] text-muted-foreground">approved by {s.approver_email} · {new Date(s.approved_at).toLocaleString()}</span>
                <span className="ml-auto text-[11px] font-mono text-muted-foreground">{s.assets?.length || 0} asset{(s.assets?.length || 0) === 1 ? "" : "s"}</span>
              </div>
              {s.hold_reason ? (
                <div className="mt-2 rounded border p-2 text-[11.5px]" style={{ background: "hsl(0 55% 96%)", borderColor: "hsl(0 55% 82%)" }}>
                  <div className="font-semibold uppercase text-[10px] tracking-wide" style={{ color: "hsl(0 65% 32%)" }}>Hold reason</div>
                  {s.hold_reason}
                </div>
              ) : null}
              {s.recipient_email ? (
                <div className="text-[11.5px] mt-1"><span className="uppercase text-[10px] tracking-wide text-muted-foreground mr-1">Recipient:</span>{s.recipient_email}</div>
              ) : null}
              {s.cover_message_subject ? (
                <div className="text-[11.5px] mt-1"><span className="uppercase text-[10px] tracking-wide text-muted-foreground mr-1">Subject:</span>{s.cover_message_subject}</div>
              ) : null}
              {s.linked_communication_id ? (
                <div className="text-[11.5px] mt-1"><span className="uppercase text-[10px] tracking-wide text-muted-foreground mr-1">Comm hub:</span><Link className="underline" to={`/advanced/communications`} data-testid={`sbe-snap-comm-${s.id}`}>{s.linked_communication_id.slice(0,8)}</Link></div>
              ) : null}
              <details className="mt-2">
                <summary className="text-[11px] cursor-pointer text-muted-foreground">Show assets ({s.assets?.length || 0})</summary>
                <ul className="text-[11.5px] mt-1 pl-3 list-disc space-y-0.5">
                  {(s.assets || []).map((a, i) => (
                    <li key={a.track_id + ":" + i}>
                      {a.title || "(untitled)"}<span className="text-muted-foreground"> · {a.artist} · {a.asset_pathway} · {a.clearance_status}{a.is_alternate ? " · alternate" : ""}</span>
                    </li>
                  ))}
                </ul>
              </details>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
